import { toast } from '@hap/components'
import { useQuery } from '@tanstack/react-query'
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'

import { useSignaling } from '~/features/signaling'
import { S3Service } from '~/services/s3'
import { queryClient, useTRPC, useTRPCClient } from '~/services/trpc'

import { UploadContext } from './context'
import { fileUploadsReducer, generateFileUploadId } from './reducers'
import { ProcessingStage, STATUS_MAP, UploadItem, UploadStatus } from './types'

export function UploadProvider({ children }: { children: React.ReactNode }) {
  const trpc = useTRPC()
  const client = useTRPCClient()
  const { subscribeToJob } = useSignaling()
  const [jobId, setJobId] = useState<string | null>(null)
  const [isPanelVisible, setIsPanelVisible] = useState(false)
  const [showUploadButton, setShowUploadButton] = useState(false)
  const [completedAt, setCompletedAt] = useState<Date | null>(null)
  const importSubscriptionsRef = useRef<Map<string, () => void>>(new Map())
  const panelTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const buttonTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [fileUploads, fileUploadDispatch] = useReducer(fileUploadsReducer, {
    ids: [],
    files: {},
    progress: 0,
    completed: 0,
  })

  const { data: activeImports } = useQuery(
    trpc.importJobs.getActive.queryOptions(undefined, {
      staleTime: 60 * 1000,
    }),
  )

  const items = useMemo<UploadItem[]>(() => {
    const serverItems = (activeImports ?? []).flatMap((job) =>
      job.files.map((file) => {
        const id =
          file.id ??
          generateFileUploadId({ jobId: job.jobId, name: file.filename })
        const serverStatus = STATUS_MAP[file.status] || 'uploaded'

        return {
          id,
          fileName: file.filename,
          status: serverStatus,
          jobId: job.jobId,
          contactLogId: file.contactLogId,
          error: file.failedReason,
        }
      }),
    )

    const serverIds = new Set(serverItems.map((item) => item.id))
    const pendingUploadItems = fileUploads.ids
      .map((id) => fileUploads.files[id])
      .filter((file): file is NonNullable<typeof file> => !!file)
      .filter((file) => !serverIds.has(file.id))
      .map<UploadItem>((file) => ({
        id: file.id,
        fileName: file.fileName,
        jobId: file.jobId,
        status: file.status,
        uploadProgress: file.progress,
        error: file.error,
      }))

    return [...serverItems, ...pendingUploadItems]
  }, [activeImports, fileUploads])

  const isUploading = fileUploads.ids.some(
    (id) => fileUploads.files[id]?.status === 'uploading',
  )
  const isComplete =
    items.length > 0 &&
    items.every((item) => item.status === 'done' || item.status === 'error')
  const hasErrors = items.some((item) => item.status === 'error')

  const activeJobIds = new Set(
    items
      .filter((item) => item.status !== 'done' && item.status !== 'error')
      .map((item) => item.jobId),
  )
  const activeImportCount = activeJobIds.size

  // Track completion and manage panel/button visibility timers
  useEffect(() => {
    const isActive = items.length > 0 && !isComplete
    const justCompleted = isComplete && items.length > 0

    if (isActive) {
      // Active uploads - show panel immediately
      setIsPanelVisible(true)
      setShowUploadButton(false)
      setCompletedAt(null)
      if (panelTimerRef.current) clearTimeout(panelTimerRef.current)
      if (buttonTimerRef.current) clearTimeout(buttonTimerRef.current)
    } else if (justCompleted) {
      if (!completedAt) {
        setCompletedAt(new Date())
        setIsPanelVisible(true)
        setShowUploadButton(false)

        // After 1 minute, hide panel and show upload button
        panelTimerRef.current = setTimeout(() => {
          setIsPanelVisible(false)
          setShowUploadButton(true)
        }, 60_000)

        // After 5 minutes total, hide upload button
        buttonTimerRef.current = setTimeout(() => {
          setShowUploadButton(false)
          setCompletedAt(null)
        }, 300_000)
      }
    } else {
      // No items - hide everything
      setIsPanelVisible(false)
      setShowUploadButton(false)
      setCompletedAt(null)
      if (panelTimerRef.current) clearTimeout(panelTimerRef.current)
      if (buttonTimerRef.current) clearTimeout(buttonTimerRef.current)
    }
  }, [items, isComplete, completedAt])

  const handleImportMessage = useCallback(
    (data: { type: string; payload?: Record<string, unknown> }) => {
      if (
        data.type !== 'update' &&
        data.type !== 'complete' &&
        data.type !== 'failed'
      ) {
        return
      }

      queryClient.invalidateQueries({
        queryKey: trpc.importJobs.getActive.queryKey(),
      })

      if (data.type === 'complete') {
        toast.success('All recordings processed successfully!')
      } else if (data.type === 'failed') {
        const failedReason =
          typeof data.payload?.failedReason === 'string'
            ? data.payload.failedReason
            : 'Unknown error'
        toast.error(`Import failed: ${failedReason}`)
      }
    },
    [trpc],
  )

  useEffect(() => {
    const desiredJobIds = new Set([
      ...(activeImports?.map((job) => job.jobId) ?? []),
      ...items
        .filter((item) => item.status !== 'done' && item.status !== 'error')
        .map((item) => item.jobId),
    ])

    for (const [
      subscribedJobId,
      unsubscribe,
    ] of importSubscriptionsRef.current.entries()) {
      if (desiredJobIds.has(subscribedJobId)) {
        continue
      }

      unsubscribe()
      importSubscriptionsRef.current.delete(subscribedJobId)
    }

    for (const jobId of desiredJobIds) {
      if (importSubscriptionsRef.current.has(jobId)) {
        continue
      }

      importSubscriptionsRef.current.set(
        jobId,
        subscribeToJob(jobId, handleImportMessage),
      )
    }
  }, [activeImports, handleImportMessage, items, subscribeToJob])

  useEffect(
    () => () => {
      for (const unsubscribe of importSubscriptionsRef.current.values()) {
        unsubscribe()
      }
      importSubscriptionsRef.current.clear()
    },
    [],
  )

  const startUploads = useCallback(
    async (files: File[]) => {
      const audioExtensions = ['.mp3', '.wav', '.m4a', '.ogg']
      const audioFiles = files.filter((file) => {
        const ext = '.' + file.name.split('.').pop()?.toLowerCase()
        return audioExtensions.includes(ext)
      })

      if (audioFiles.length === 0) return

      try {
        const result = await client.importJobs.create.mutate({
          filenames: audioFiles.map((f) => f.name),
        })
        const filesWithIds = audioFiles.map((file, index) => ({
          id:
            result.files[index]?.id ??
            generateFileUploadId({ jobId: result.jobId, name: file.name }),
          file,
        }))

        setJobId(result.jobId)

        fileUploadDispatch({
          type: 'APPEND_UPLOADS',
          payload: {
            jobId: result.jobId,
            files: filesWithIds,
          },
        })

        await Promise.all(
          filesWithIds.map(async ({ id, file }) => {
            try {
              const { uploadUrl } =
                await client.importJobs.initiateUpload.mutate({
                  fileId: id,
                  filename: file.name,
                  jobId: result.jobId,
                })

              await S3Service.upload(file, uploadUrl, (uploadProgress) => {
                fileUploadDispatch({
                  type: 'UPDATE_UPLOAD_PROGRESS',
                  payload: { id, progress: uploadProgress },
                })
              })
              fileUploadDispatch({
                type: 'COMPLETE_UPLOAD',
                payload: { id },
              })
            } catch (error) {
              fileUploadDispatch({
                type: 'FAIL_UPLOAD',
                payload: {
                  id,
                  error:
                    error instanceof Error ? error.message : 'Upload failed',
                },
              })
              throw error
            }
          }),
        )

        await queryClient.invalidateQueries({
          queryKey: trpc.importJobs.getActive.queryKey(),
        })
      } catch (error) {
        toast.error('Failed to start import job')
      }
    },
    [client, trpc],
  )

  const statusToStage: Record<UploadStatus, ProcessingStage> = {
    pending: 'upload',
    uploading: 'upload',
    uploaded: 'upload',
    importing: 'import',
    transcribing: 'transcribe',
    analyzing: 'analyze',
    done: 'done',
    error: 'done',
  }

  const stageOrder: ProcessingStage[] = [
    'upload',
    'import',
    'transcribe',
    'analyze',
    'done',
  ]

  const activeItems = items.filter(
    (item) => item.status !== 'done' && item.status !== 'error',
  )
  const importProgress = useMemo(() => {
    const statusWeights: Record<UploadStatus, number> = {
      pending: 0,
      uploading: 0,
      uploaded: 20,
      importing: 35,
      transcribing: 60,
      analyzing: 85,
      done: 100,
      error: 100,
    }

    if (items.length === 0) {
      return { percent: 0, completed: 0, total: 0 }
    }

    const weightedTotal = items.reduce((total, item) => {
      const weight =
        item.status === 'uploading'
          ? Math.round(((item.uploadProgress ?? 0) / 100) * 10)
          : statusWeights[item.status]

      return total + weight
    }, 0)

    return {
      percent: Math.round(weightedTotal / items.length),
      completed: items.filter(
        (item) => item.status === 'done' || item.status === 'error',
      ).length,
      total: items.length,
    }
  }, [items])
  const furthestStage: ProcessingStage | null =
    activeItems.length === 0
      ? null
      : activeItems.reduce<ProcessingStage>((furthest, item) => {
          const stage = statusToStage[item.status]
          const currentIndex = stageOrder.indexOf(stage)
          const furthestIndex = stageOrder.indexOf(furthest)
          return currentIndex > furthestIndex ? stage : furthest
        }, 'upload')

  return (
    <UploadContext.Provider
      value={{
        items,
        importProgress,
        isUploading,
        isComplete,
        hasErrors,
        startUploads,
        jobId,
        activeImportCount,
        furthestStage,
        isPanelVisible,
        showUploadButton,
        fileUploads: fileUploads,
      }}
    >
      {children}
    </UploadContext.Provider>
  )
}
